<script setup lang="ts">
import {computed, ComputedRef, ref, Ref, watch, watchEffect, onErrorCaptured, onMounted, onUnmounted, nextTick, provide} from "vue"

import {PuzzleFile} from "~lib"

import {setApi, clearApi, setSaveManager, clearSaveManager, taskRunner, title} from "~/ui/globals.ts"
import {SaveManager} from "~/ui/SaveManager"
import {Storage, getStorageInstances, PuzzleNotFoundError, StorageId} from "~/ui/storage.ts"
import {Action, GridSetAction} from "~/ui/actions.ts"
import {UiButtonDefinition, useUiButtonComposible} from "~/ui/ui-buttons.ts"
import {PuzzleStudioApi} from "~/ui/api.ts"
import TitleBar from "~/ui/components/TitleBar.vue"
import PuzzleEditor from "~/ui/components/PuzzleEditor.vue"
import Modal from "~/ui/common/Modal.vue"
import RawDataModal from "~/ui/components/RawDataModal.vue"
import PuzzleSaveModal from "~/ui/components/PuzzleSaveModal.vue"
import ExportModal from "~/ui/components/ExportModal.vue"
import PuzzleMetadataModal from "~/ui/components/PuzzleMetadataModal.vue"
import GridEditModal from "~/ui/components/GridEditModal.vue"
import UiButton from "~/ui/components/UiButton.vue"

const props = defineProps<{
    storageId: StorageId,
    puzzleName: string,
}>()

const storage: Ref<Storage | null> = computed(
    () => getStorageInstances()[props.storageId] || null
)
const puzzleFile: Ref<PuzzleFile | null> = ref(null)
const saveManager = new SaveManager(props.puzzleName, storage, puzzleFile)
provide("saveManager", saveManager)

onMounted(() => {
    setApi(new PuzzleStudioApi(puzzleEditor, uiButtons))
    setSaveManager(saveManager)
})

onUnmounted(() => {
    clearApi()
    clearSaveManager()
})

type PuzzleErrorInfo = {
    title: string,
    recoverable: boolean,
    errorMessage: string,
    userMessage?: string,
}
const puzzleError: Ref<PuzzleErrorInfo | null> = ref(null)
const puzzleErrorModal: Ref<InstanceType<typeof Modal> | null> = ref(null)

const puzzleEditor: Ref<InstanceType<typeof PuzzleEditor> | null> = ref(null)
const metadataModal: Ref<InstanceType<typeof PuzzleMetadataModal> | null> = ref(null)
const rawDataModal: Ref<InstanceType<typeof RawDataModal> | null> = ref(null)
const exportModal: Ref<InstanceType<typeof ExportModal> | null> = ref(null)
const saveModal: Ref<InstanceType<typeof PuzzleSaveModal> | null> = ref(null)
const gridEditModal: Ref<InstanceType<typeof GridEditModal> | null> = ref(null)

watch(puzzleError, () => {
    nextTick(() => {
        if(puzzleError.value === null) {
            puzzleErrorModal.value?.close()
        } else {
            puzzleErrorModal.value?.open()
        }
    })
})

watchEffect(() => {
    title.value = props.puzzleName
})

void setPuzzleFile()

const uiButtons = useUiButtonComposible(
    puzzleFile,
    storage,
    saveManager,
    performAction,
    puzzleEditor,
    saveModal,
    metadataModal,
    rawDataModal,
    exportModal,
    gridEditModal,
)

provide("uiButtons", uiButtons)

async function setPuzzleFile(ignoreErrors=false) {
    puzzleError.value = null
    puzzleFile.value = null
    if(!storage.value) {
        puzzleError.value = {
            title: "Storage not found",
            errorMessage: `The storage not found: "${props.storageId}"`,
            userMessage: "Return home to select another puzzle.",
            recoverable: false,
        }
        return
    }
    try {
        puzzleFile.value = await storage.value.get(props.puzzleName, ignoreErrors)
    } catch(e) {
        console.error(e)
        if(e instanceof PuzzleNotFoundError) {
            puzzleError.value = {
                title: "Puzzle not found",
                errorMessage: stripIfStartsWith(String(e), "Error:"),
                userMessage: storage.value.notFoundErrorMessage + "\n\nReturn home to select another puzzle.",
                recoverable: false,
            }
            return
        }

        // Try again immediately with ignoreErrors set.
        // If it fails now, we know it's unrecoverable.
        let recoverable = true
        try {
            await storage.value.get(props.puzzleName, true)
        } catch {
            recoverable = false
        }

        puzzleError.value = {
            title: "Puzzle read error",
            errorMessage: stripIfStartsWith(String(e), "Error:"),
            recoverable: recoverable,
        }
    }

    if(puzzleFile.value?.needsInitialConfigure) {
        nextTick(() => {
            if(puzzleFile.value) {
                gridEditModal.value?.open()
            }
        })
    }
}

function performAction(action: Action) {

    if(action instanceof GridSetAction && puzzleFile.value) {
        puzzleFile.value.needsInitialConfigure = undefined
    }

    try {
        saveManager.performAction(action)
    } catch(e) {
        const msg = `Error performing action: ${action.toString()}`
        errorShow.value = true
        errorMessage.value = msg + "\n" + stripIfStartsWith(String(e), "Error:")
        console.error(msg, "\n", e)
    }
}

onErrorCaptured((error: unknown) => {
    console.error(error)
    puzzleError.value = {
        title: "Puzzle render error",
        errorMessage: stripIfStartsWith(String(error), "Error:"),
        recoverable: false,
    }
    puzzleFile.value = null
    return false
})

const errorShow = ref(false)
const errorMessage = ref("")

function stripIfStartsWith(input: string, toStrip: string) {
    return input.startsWith(toStrip) ?
        input.slice(toStrip.length).trimStart()
        : input
}

let oldFinishedLength = 0
watch(taskRunner.finished, () => {
    // Only show snackbar if new task was added to `finished` list
    const newLength = taskRunner.finished.length
    if(newLength === oldFinishedLength) {
        return
    }
    oldFinishedLength = newLength

    const finished = taskRunner.finished[taskRunner.finished.length-1]
    if(finished.error !== null) {
        errorShow.value = true
        errorMessage.value = finished.error
    }
})

type Menu = {
    text: string,
    items: UiButtonDefinition[],
}

const menus: ComputedRef<Menu[]> = computed(() => {
    let shapeOrProblemMenuItems: UiButtonDefinition[]
    if(puzzleEditor.value?.currentTabId === "shapes") {
        shapeOrProblemMenuItems = [
            uiButtons.newShape,
            uiButtons.deleteSelectedItem,
            uiButtons.duplicateShape,
        ]
    } else {
        shapeOrProblemMenuItems = [
            uiButtons.newProblem,
            uiButtons.deleteSelectedItem,
            uiButtons.duplicateProblem,
        ]
    }

    return [
        {
            text: "Puzzle",
            items: [
                uiButtons.newPuzzle,
                uiButtons.puzzleMetadata,
                uiButtons.gridEdit,
                uiButtons.puzzleRawData,
                uiButtons.downloadPuzzle,
                uiButtons.export,
                uiButtons.saveAs,
            ],
        },
        {
            text: "Edit",
            items: [
                uiButtons.undo,
                uiButtons.redo,
                ...shapeOrProblemMenuItems
            ],
        },
        {
            text: "Groups",
            items: [
                uiButtons.newShapeAssembly,
            ],
        },
    ]
})

const toolbarButtons: UiButtonDefinition[] = [
    uiButtons.undo,
    uiButtons.redo,
]
</script>

<template>
    <TitleBar :puzzleName="puzzleName" :puzzleFile="puzzleFile" :storage="storage" flat />
    <VAppBar
        density="compact"
        :height="48"
        class="toolbar"
    >

        <div class="toolbar-left">
            <VMenu v-for="menu in menus">
                <template v-slot:activator="{props}">
                    <VBtn v-bind="props">{{ menu.text }}</VBtn>
                </template>
                <VList>
                    <VListItem
                        v-for="menuItem in menu.items"
                        @click="menuItem.perform"
                        :disabled="menuItem.enabled === undefined ? false : !menuItem.enabled()"
                    >
                        <VIcon v-if="menuItem.icon" :icon="menuItem.icon" :aria-label="menuItem.text" aria-hidden="false" />
                        {{ typeof menuItem.text === 'string' ? menuItem.text : menuItem.text() }}
                    </VListItem>
                </VList>
            </VMenu>
        </div>

        <div class="toolbar-center">
            <UiButton
                v-for="toolbarButton in toolbarButtons"
                :uiButton="toolbarButton"
            />
        </div>

        <div class="toolbar-right">
        </div>

    </VAppBar>
    <PuzzleEditor
        v-if="puzzleFile"
        ref="puzzleEditor"
        :puzzle="puzzleFile.puzzle"
        @action="performAction"
    />
    <VSnackbar
        v-model="errorShow"
        timeout="5000"
        color="red"
        style="white-space: pre;"
    >
        {{ errorMessage }}
    </VSnackbar>

    <Modal
        ref="puzzleErrorModal"
        @ok="puzzleErrorModal?.close(); void setPuzzleFile(true)"
        @cancel="$router.push({name: 'home'})"
        :title="puzzleError?.title || ''"
        persistent
        okText="Continue"
        :okShow="puzzleError?.recoverable"
        cancelText="Home"
    >
        <code>{{ puzzleError?.errorMessage }}</code>

        <p v-if="puzzleError?.userMessage" style="white-space: pre-wrap;">
            {{ puzzleError?.userMessage }}
        </p>
        <p v-else-if="puzzleError?.recoverable">
            If you continue, things may not work correctly.
        </p>
        <p v-else>
            Since this error cannot be ignored, the puzzle cannot be viewed.
        </p>
    </Modal>

    <PuzzleMetadataModal
        v-if="puzzleFile && storage"
        ref="metadataModal"
        :puzzleName="puzzleName"
        :puzzleFile="puzzleFile"
        :storage="storage"
        @action="performAction"
    />
    <RawDataModal ref="rawDataModal" />
    <PuzzleSaveModal ref="saveModal" />
    <GridEditModal
        v-if="puzzleFile"
        ref="gridEditModal"
        :puzzle="puzzleFile.puzzle"
        :initialConfig="puzzleFile.needsInitialConfigure || false"
        @action="performAction($event)"
    />
    <ExportModal
        v-if="puzzleFile"
        ref="exportModal"
        :puzzleFile="puzzleFile"
    />
</template>

<style scoped>
.toolbar {
    align-items: center;
}
.toolbar div {
    flex-basis: 0;
    flex-grow: 1;
}
.toolbar .toolbar-center {
    text-align: center;
}
.toolbar .toolbar-right {
    text-align: right;
}
</style>